/**
 * Moving a drawn element to where the document says it was put by hand.
 *
 * An offset is written into the MusicXML the engine is given -- `relative-x` and
 * `relative-y`, which is the format's own way of saying it -- and that is what another
 * program reads. It is not what *this* engine reads: Verovio lays a page out from the music
 * alone and ignores both attributes, so a nudge that lived only in the file would be
 * invisible in the editor that made it. It is therefore applied here, to the markup the
 * engine handed back, as a transform on the group the element was drawn in.
 *
 * That is honest about what a nudge is. The engraver places the note; this moves the drawn
 * result afterwards and moves nothing else, so nothing is re-spaced around it and the next
 * relayout starts from the engraver's own placing again. It is the last word on the page
 * rather than a hint to the machine that draws it.
 *
 * The markup is patched as text rather than parsed. A page of engraved music is a large
 * tree to build in order to add an attribute to a handful of elements in it, and the
 * elements in question are exactly the ones this package stamped its own ids onto, so they
 * can be found by those ids without knowing anything else about the document.
 */

import type { EventId } from "../document/ids";
import type { Offset, Score } from "../document/types";
import { CHORD_SEPARATOR } from "./ids";

/** Every event that has been moved, by id. Empty for a score nobody has nudged. */
export function offsetsOf(score: Score): ReadonlyMap<EventId, Offset> {
  const found = new Map<EventId, Offset>();
  for (const part of score.parts) {
    for (const measure of Object.values(part.measures)) {
      for (const voice of measure.voices) {
        for (const event of voice.events) {
          if (event.offset !== null) found.set(event.id, event.offset);
        }
      }
    }
  }
  return found;
}

/**
 * Engine units per tenth.
 *
 * The engine's unit is half the space between two staff lines and its SVG is written in
 * tenths of that unit, so a staff space is twenty units of markup and a MusicXML tenth --
 * one tenth of a staff space -- is two. Measured against the drawn output rather than
 * assumed: a five-line staff at the default unit of 9 comes back with its lines 180 apart.
 */
function perTenth(unit: number): number {
  return unit * 2;
}

/** The part of a drawn element's id before the chord suffix, if it has one. */
function stem(elementId: string): string {
  const cut = elementId.indexOf(CHORD_SEPARATOR);
  return cut === -1 ? elementId : elementId.slice(0, cut);
}

/**
 * The same markup with every moved element moved.
 *
 * A chord is one event drawn as several note heads, each carrying the event's id with a
 * suffix, and all of them take the same translation: the chord moves as a chord.
 */
export function nudged(svg: string, offsets: ReadonlyMap<EventId, Offset>, unit: number): string {
  if (offsets.size === 0) return svg;
  const scale = perTenth(unit);
  return svg.replace(/<g id="([^"]+)"([^>]*)>/g, (whole, id: string, rest: string) => {
    const offset = offsets.get(stem(id) as EventId);
    if (offset === undefined) return whole;
    // The document counts y upwards, as MusicXML does, and the page counts it downwards.
    const move = `translate(${Math.round(offset.x * scale)}, ${Math.round(-offset.y * scale)})`;
    const held = /transform="([^"]*)"/.exec(rest);
    if (held === null) return `<g id="${id}"${rest} transform="${move}">`;
    // An engine that already placed the group by transform keeps its own placing; ours is
    // written first so that it is applied last, which moves the element from where it sat.
    return `<g id="${id}"${rest.replace(held[0], `transform="${move} ${held[1] ?? ""}"`)}>`;
  });
}
